import styled, { css } from 'styled-components';
import { getHandleActionKeyDown } from '@elfsight/widget-utils';
import classNames from 'classnames';
import { useSlider } from '../../SliderContext';
import { Count } from '../Count';
import { relativePositionAffixes } from '../constants';
import { Progress } from '../Progress';

interface BulletProps {
  index: number;
  currentIndex: number;
  slidesLength: number;
  paginationClassName: string;
  autoplayProgressBar: boolean;
  autoplayEnabled: boolean;
  autoplayPauseOnHover: boolean;
  goTo: (index: number) => void;
  seconds: number;
  active: boolean;
  last: boolean;
  color: string;
  withShadow: boolean;
}

export const Bullet = ({
  index,
  currentIndex,
  slidesLength,
  paginationClassName,
  autoplayProgressBar,
  autoplayEnabled,
  autoplayPauseOnHover,
  goTo,
  seconds,
  active,
  last,
  color,
  withShadow
}: BulletProps) => {
  const { appClassnamePrefix } = useSlider();

  const positionAffix = relativePositionAffixes[index - currentIndex + 2];

  return (
    <StyledBullet
      role="button"
      tabIndex={active ? -1 : 0}
      aria-label={`Slide ${index + 1} of ${slidesLength}`}
      onClick={() => goTo(index)}
      onKeyDown={getHandleActionKeyDown(() => goTo(index))}
      _paginationClass={paginationClassName}
      _appClassPrefix={appClassnamePrefix}
      _positionAffix={positionAffix}
      _isActive={active}
    >
      {autoplayProgressBar && autoplayEnabled && (
        <Count
          active={active}
          seconds={seconds}
          autoplayProgressBar={autoplayProgressBar}
          autoplayPauseOnHover={autoplayPauseOnHover}
          last={last}
        >
          {({ percent }: { percent: number }) => (
            <Progress.Circle
              percent={percent}
              radius={7}
              stroke={1}
              color={color}
              appClassnamePrefix={appClassnamePrefix}
            />
          )}
        </Count>
      )}

      <Dot
        _paginationClass={paginationClassName}
        _appClassPrefix={appClassnamePrefix}
        _isActive={active}
        _withShadow={withShadow}
      />
    </StyledBullet>
  );
};

interface DotProps {
  _paginationClass: string;
  _appClassPrefix: string;
  _isActive: boolean;
  _withShadow: boolean;
}

const Dot = styled.div.attrs<DotProps>(
  ({ _paginationClass, _appClassPrefix }) => ({
    className: classNames(
      { [`${_paginationClass}-dot`]: _paginationClass },
      `${_appClassPrefix}-bullet-dot`
    )
  })
)`
  border-radius: 50%;
  transform: scale(1);
  width: 8px;
  min-width: 8px;
  height: 8px;
  margin: 0 8px;
  transition: 0.3s all ease-out;
  opacity: 0.7;

  ${({ _withShadow }) =>
    _withShadow && 'box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);'}

  ${({ _isActive }) =>
    _isActive &&
    css`
      transform: scale(1);
      opacity: 1;
    `}
`;

interface StyledBulletProps {
  _paginationClass: string;
  _appClassPrefix: string;
  _positionAffix: string;
  _isActive: boolean;
}

const StyledBullet = styled.div.attrs<StyledBulletProps>(
  ({ _paginationClass, _appClassPrefix, _positionAffix, _isActive }) => ({
    className: classNames(`${_appClassPrefix}-bullet-item`, {
      [`${_appClassPrefix}-bullet-${_positionAffix}`]: _positionAffix,
      [`${_paginationClass}-item`]: _paginationClass,
      [`${_paginationClass}-item-${
        _isActive ? '' : 'active-'
      }${_positionAffix}`]: _paginationClass && _positionAffix
    })
  })
)`
  display: inline-block;
  position: relative;
  transition: 0.3s all ease-out;
  cursor: pointer;
  padding-top: 8px;
  padding-bottom: 8px;

  &:hover ${Dot} {
    transition: 0.1s all ease-out;
    transform: scale(1);
    opacity: 1;
  }
`;
